import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { WordPressPost } from '../types/wordpress';
import { fetchPosts, fetchPostById, fetchPostBySlug, fetchRelatedPosts } from '../services/api';
import EmbeddedAdSense from './EmbeddedAdSense';
import { processContentForDisplay } from '../utils/textUtils';

import { useResponsiveImage } from '../hooks/useOptimizedImage';
import { useLanguageContext } from '../contexts/LanguageContext';
import { formatDate } from '../utils/dateUtils';

// ✅ NEW: Build language-aware API URL
const buildLanguageAwareUrl = (baseUrl: string, languageCategoryId?: string): string => {
  if (!languageCategoryId) return baseUrl;
  
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('categories', languageCategoryId);
  
  return url.toString();
};

// ======================== TAG BATCH MANAGER (ANTI-SPAM SOLUTION) ========================
class TagBatchManager {
  private static instance: TagBatchManager;
  private cache = new Map<number, any>();
  private pendingRequests = new Map<number, Promise<any>>();
  private batchQueue = new Set<number>();
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_DELAY = 100; // ms
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private languageCategoryId?: string; // ✅ NEW: Store language context

  static getInstance(): TagBatchManager {
    if (!TagBatchManager.instance) {
      TagBatchManager.instance = new TagBatchManager();
    }
    return TagBatchManager.instance;
  }

  // ✅ NEW: Set language context
  setLanguageContext(languageCategoryId?: string) {
    this.languageCategoryId = languageCategoryId;
  }

  async getTag(tagId: number): Promise<any> {
    // Check cache first
    const cached = this.cache.get(tagId);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      return cached.data;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(tagId)) {
      return this.pendingRequests.get(tagId);
    }

    // Add to batch queue
    this.batchQueue.add(tagId);
    
    // Return a promise that resolves when the batch request completes
    const promise = new Promise((resolve) => {
      const checkForResult = () => {
        const result = this.cache.get(tagId);
        if (result) {
          resolve(result.data);
        } else {
          // Check again after a short delay
          setTimeout(checkForResult, 50);
        }
      };
      
      // Schedule batch request
      this.scheduleBatchRequest();
      
      // Start checking for result
      checkForResult();
    });

    this.pendingRequests.set(tagId, promise);
    return promise;
  }

  private scheduleBatchRequest() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.executeBatchRequest();
    }, this.BATCH_DELAY);
  }

  private async executeBatchRequest() {
    if (this.batchQueue.size === 0) return;

    const tagIds = Array.from(this.batchQueue);
    this.batchQueue.clear();
    
    console.log(`[TagBatchManager] 🚀 Batch request for ${tagIds.length} tags:`, tagIds, {
      languageCategoryId: this.languageCategoryId
    });

    try {
      // Try batch API first with language filtering
      if (tagIds.length > 1) {
        const baseUrl = `/api/tags?include=${tagIds.join(',')}&per_page=${tagIds.length}`;
        const batchResponse = await fetch(buildLanguageAwareUrl(baseUrl, this.languageCategoryId));
        if (batchResponse.ok) {
          const tags = await batchResponse.json();
          
          // Cache all results
          tags.forEach((tag: any) => {
            this.cache.set(tag.id, {
              data: tag,
              timestamp: Date.now()
            });
            this.pendingRequests.delete(tag.id);
          });
          
          // Handle missing tags
          tagIds.forEach(tagId => {
            if (!tags.find((t: any) => t.id === tagId)) {
              this.cache.set(tagId, {
                data: null,
                timestamp: Date.now()
              });
              this.pendingRequests.delete(tagId);
            }
          });
          
          console.log(`[TagBatchManager] ✅ Batch request completed, cached ${tags.length} tags (language filtered)`);
          return;
        }
      }

      // Fallback to individual requests with language filtering
      const individualPromises = tagIds.map(async (tagId) => {
        try {
          const baseUrl = `/api/tags/${tagId}`;
          const response = await fetch(buildLanguageAwareUrl(baseUrl, this.languageCategoryId));
          if (response.ok) {
            const tag = await response.json();
            this.cache.set(tagId, {
              data: tag,
              timestamp: Date.now()
            });
            this.pendingRequests.delete(tagId);
            return tag;
          }
        } catch (error) {
          console.error(`Error fetching tag ${tagId}:`, error);
        }
        
        this.cache.set(tagId, {
          data: null,
          timestamp: Date.now()
        });
        this.pendingRequests.delete(tagId);
        return null;
      });
      
      await Promise.all(individualPromises);
    } catch (err) {
      console.error('[TagBatchManager] ❌ Error in batch request:', err);
      
      // Clear pending requests on error
      tagIds.forEach(tagId => {
        this.cache.set(tagId, {
          data: null,
          timestamp: Date.now()
        });
        this.pendingRequests.delete(tagId);
      });
    }
  }

  // Get cache stats for debugging
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      batchQueueSize: this.batchQueue.size,
      languageCategoryId: this.languageCategoryId
    };
  }

  // Clear cache if needed
  clearCache() {
    this.cache.clear();
    console.log('[TagBatchManager] 🧹 Cache cleared');
  }
}

// ✅ UPDATED: Global Audio Player State Interface with lazy loading support
interface GlobalAudioState {
  isPlaying: boolean;
  currentPostId: number | null;
  currentAudioIndex: number;
  currentAudio: HTMLAudioElement | null;
  playlist: Array<{
    postId: number;
    audioUrls: string[]; // ✅ Will be populated lazily on play intent
    title: string;
    hasAudio: boolean; // ✅ NEW: Lightweight flag to avoid URL extraction
    content: string; // ✅ NEW: Store content for lazy extraction
  }>;
}

// Smooth scroll configuration
const SCROLL_CONFIG = {
  behavior: 'smooth' as ScrollBehavior,
  block: 'start' as ScrollLogicalPosition,
  inline: 'nearest' as ScrollLogicalPosition
};

// Throttle function for scroll events
const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean;
  return function(this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

// Container with smooth scrolling
const Container = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  scroll-behavior: smooth;
  overflow-x: hidden;
  /* Ensure sticky context for children */
  position: relative;
`;

// Header components
const Header = styled.header`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Logo = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 50%, #667eea 100%);
    border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const LogoAccent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AtomOrbit = styled.div<{ 
  $color: string; 
  $size: number; 
  $duration: number; 
  $direction?: 'normal' | 'reverse' 
}>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size * 0.6}px;
  border: 1.5px solid ${props => props.$color};
  border-radius: 50%;
  animation: rotate ${props => props.$duration}s linear infinite ${props => props.$direction || 'normal'};
  opacity: 0.8;
  
  &::before {
    content: '';
    position: absolute;
    width: 3px;
    height: 3px;
    background: ${props => props.$color};
    border-radius: 50%;
    top: -1.5px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 0 4px ${props => props.$color};
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const BrandText = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-family: 'Segoe UI', sans-serif;
  
  .infonium {
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  
  .org {
    font-size: 1rem;
    font-weight: 600;
    color: #e53e3e;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }
`;

const NavigationSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const PostCounter = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Breadcrumb
const Breadcrumb = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 24px 0 24px;
  font-size: 0.9rem;
  color: #718096;
  
  a {
    color: #667eea;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 8px;
  }
`;

// Main content layout
const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 900px) {
    display: block; /* Use block layout on mobile for sidebar */
    padding: 20px 16px;
  }

  @media (max-width: 600px) {
    padding: 16px 12px;
  }
`;

const ArticleSection = styled.section`
  width: 100%;
  max-width: calc(100% - 470px); /* Extra comfortable reading space (350px + 120px gap) */
  margin-right: 60px; /* Extra comfortable gap from sidebar */
  
  @media (max-width: 1200px) {
    max-width: calc(100% - 420px); /* Adjust for smaller sidebar (300px + 120px gap) */
    margin-right: 60px;
  }
  
  @media (max-width: 900px) {
    max-width: 100%;
    margin-right: 0; /* No margin on mobile - sidebar is below */
  }
`;

const SidebarSection = styled.aside`
  width: 350px; /* Fixed width for positioning */
  position: fixed; /* Use fixed positioning for guaranteed stickiness */
  top: 100px;
  right: max(60px, calc((100vw - 1400px) / 2 + 84px)); /* Extra comfortable spacing from edge */
  height: calc(100vh - 120px);
  z-index: 50;
  will-change: transform; /* Optimize for animations */
  
  @media (max-width: 1200px) {
    width: 300px;
    top: 90px;
    right: max(60px, calc((100vw - 1200px) / 2 + 84px)); /* Extra comfortable spacing */
    height: calc(100vh - 110px);
  }
  
  @media (max-width: 900px) {
    position: relative; /* Back to normal flow on mobile */
    width: 100%;
    top: auto;
    right: auto;
    height: auto;
    min-height: 400px;
    margin-top: 30px;
  }
  
  @media (max-width: 600px) {
    margin-top: 20px;
    min-height: 300px;
  }
`;

// Post components
const PostContainer = styled.article<{ $isCurrent?: boolean }>`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  margin: 40px 0;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: ${props => props.$isCurrent ? '3px solid #667eea' : '3px solid transparent'};
  transform: ${props => props.$isCurrent ? 'scale(1.02)' : 'scale(1)'};
  
  &:hover {
    transform: ${props => props.$isCurrent ? 'scale(1.02)' : 'scale(1.01)'};
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
  
  scroll-margin-top: 100px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  overflow: hidden;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
`;

const PostImage = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.5s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transform: ${props => props.$isLoaded ? 'scale(1)' : 'scale(1.1)'};
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 3rem;
  
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`;

const PostContent = styled.div`
  padding: 40px;
`;

const PostTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #2d3748;
  margin: 0 0 20px 0;
  line-height: 1.2;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 0 0 30px 0;
  padding: 0 0 20px 0;
  border-bottom: 2px solid #e2e8f0;
`;

const PostDate = styled.span`
  color: #718096;
  font-weight: 500;
`;

const PostNumber = styled.span`
  color: #667eea;
  font-weight: 600;
  font-size: 0.85rem;
  background: rgba(102, 126, 234, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
`;

const PostContentBody = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4a5568;
  
  p {
    margin: 0 0 20px 0;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    margin: 20px 0;
  }
  
  h2, h3, h4 {
    color: #2d3748;
    margin: 30px 0 15px 0;
  }
  
  blockquote {
    border-left: 4px solid #667eea;
    padding: 20px;
    margin: 20px 0;
    background: #f7fafc;
    border-radius: 0 12px 12px 0;
  }
`;

const PostSeparator = styled.div`
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, #e2e8f0 20%, #e2e8f0 80%, transparent 100%);
  margin: 30px 0 0 0;
`;

// PC Single Post Hashtags Section
const PCHashtagContainer = styled.div`
  margin: 30px 0 0 0;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const PCHashtagTitle = styled.h3`
  color: #2d3748;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '🏷️';
    font-size: 20px;
  }
`;

const PCHashtagGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PCHashtagItem = styled.button<{ $isPopular?: boolean }>`
  background: ${props => props.$isPopular 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  border: 1px solid ${props => props.$isPopular ? '#667eea' : '#e2e8f0'};
  border-radius: 25px;
  padding: 10px 18px;
  color: ${props => props.$isPopular ? '#fff' : '#2d3748'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &::before {
    content: '#';
    color: ${props => props.$isPopular ? '#fff' : '#667eea'};
    font-weight: bold;
    font-size: 16px;
  }

  &:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.25);
    
    &::before {
      color: #fff;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const PCHashtagLoadingSkeleton = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PCHashtagSkeletonItem = styled.div<{ $width?: number }>`
  background: linear-gradient(90deg, #f0f2f5 25%, #e4e7eb 50%, #f0f2f5 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 25px;
  height: 40px;
  width: ${props => props.$width || 100}px;

  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`;

// Sidebar components
const SidebarContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  height: 100%; /* Take full height of sticky parent */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative; /* Ensure proper positioning context */
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
`;

const SidebarTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 10px 0;
`;

const GlobalAudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #718096;
`;

const GlobalPlayButton = styled.button<{ $isPlaying?: boolean }>`
  background: ${props => props.$isPlaying ? 'linear-gradient(135deg, #38a169 0%, #48bb78 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const GlobalPlaylistInfo = styled.div`
  font-size: 0.75rem;
  color: #a0aec0;
`;

const PlaylistContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  min-height: 0; /* Important for flex child to scroll */
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

const PlaylistItem = styled.div<{ $isCurrent?: boolean; $isPlaying?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f1f5f9;
  background: ${props => {
    if (props.$isPlaying) return 'linear-gradient(135deg, #38a169 0%, #48bb78 100%)';
    if (props.$isCurrent) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    return 'transparent';
  }};
  color: ${props => (props.$isCurrent || props.$isPlaying) ? 'white' : '#2d3748'};
  
  &:hover {
    background: ${props => {
      if (props.$isPlaying) return 'linear-gradient(135deg, #38a169 0%, #48bb78 100%)';
      if (props.$isCurrent) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      return '#f8fafc';
    }};
    transform: translateX(4px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const PlaylistNumber = styled.div<{ $isCurrent?: boolean; $isPlaying?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => {
    if (props.$isPlaying) return 'rgba(255, 255, 255, 0.2)';
    if (props.$isCurrent) return 'rgba(255, 255, 255, 0.2)';
    return '#e2e8f0';
  }};
  color: ${props => (props.$isCurrent || props.$isPlaying) ? 'white' : '#718096'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    background: ${props => {
      if (props.$isPlaying) return 'rgba(255, 255, 255, 0.3)';
      if (props.$isCurrent) return 'rgba(255, 255, 255, 0.3)';
      return '#cbd5e0';
    }};
  }
`;

const PlaylistImageContainer = styled.div`
  width: 120px;
  height: 68px; /* 16:9 aspect ratio for better content visibility */
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.2s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  .no-image {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    color: #718096;
  }
`;

// Optimized Playlist Image Component
interface PlaylistImageProps {
  src: string;
  alt: string;
  isMobile?: boolean;
}

const PlaylistImage: React.FC<PlaylistImageProps> = ({ src, alt, isMobile = false }) => {
  // Use optimized image for sidebar thumbnails
  const optimizedImage = useResponsiveImage(src, 'related-posts-grid', isMobile);
  
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log('Sidebar image failed (optimized):', optimizedImage.src);
    const target = e.target as HTMLImageElement;
    target.src = 'https://cdn.infonium.org/img/logo_infonium.png';
  };

  const handleLoad = () => {
    console.log('Sidebar image loaded (optimized):', {
      original: src,
      optimized: optimizedImage.src,
      isOptimized: optimizedImage.isOptimized,
      size: optimizedImage.size
    });
  };

  return (
    <PlaylistImageContainer>
      <img 
        src={optimizedImage.src} 
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        title={`${alt} - ${optimizedImage.isOptimized ? 'Optimized' : 'Original'} (${optimizedImage.size})`}
      />
    </PlaylistImageContainer>
  );
};

const PlaylistInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlaylistTitle = styled.h4<{ $isCurrent?: boolean; $isPlaying?: boolean }>`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => (props.$isCurrent || props.$isPlaying) ? 'white' : '#2d3748'};
  margin: 0 0 4px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PlaylistDate = styled.span<{ $isCurrent?: boolean; $isPlaying?: boolean }>`
  font-size: 0.75rem;
  color: ${props => (props.$isCurrent || props.$isPlaying) ? 'rgba(255, 255, 255, 0.8)' : '#718096'};
  font-weight: 500;
`;

// Loading and utility components
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
    min-height: 400px;
  font-size: 1.1rem;
  color: #718096;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #718096;
  font-size: 1rem;
  gap: 16px;
`;

const EndOfContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  border-top: 2px solid #e2e8f0;
  margin-top: 40px;
  gap: 16px;
  color: #a0aec0;
  font-size: 0.9rem;
`;

const BackToTopButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Smooth scroll progress indicator
const ScrollProgress = styled.div<{ $progress: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: ${props => props.$progress}%;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  z-index: 1001;
  transition: width 0.1s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
`;

// Custom Audio Player Component
interface AudioPlayerProps {
  audioUrls: string[];
  postTitle: string;
  postId: number;
  globalAudio: GlobalAudioState;
  onPlayAudio: (postId: number) => void;
  onStopAudio: () => void;
}

const AudioPlayerContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 16px 20px;
  margin: 20px 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  }
`;

const PlayButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #667eea;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const AudioInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AudioTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.95;
`;

const AudioSubtitle = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
`;

const CustomAudioPlayer: React.FC<AudioPlayerProps> = ({ 
  audioUrls, 
  postTitle, 
  postId, 
  globalAudio,
  onPlayAudio,
  onStopAudio
}) => {
  const isCurrentlyPlaying = globalAudio.isPlaying && globalAudio.currentPostId === postId;

  const handlePlayPause = () => {
    if (isCurrentlyPlaying) {
      onStopAudio();
    } else {
      onPlayAudio(postId);
    }
  };

  // ✅ ENHANCED LAZY LOADING: Show audio player UI but don't create ANY audio elements during render
  const playlistItem = globalAudio.playlist.find(item => item.postId === postId);
  if (!playlistItem || !playlistItem.hasAudio) return null;

  // ✅ CRITICAL FIX: This component only renders UI - no audio elements created during scroll
  return (
    <AudioPlayerContainer onClick={handlePlayPause}>
      <PlayButton>
        {isCurrentlyPlaying ? '⏸' : '▶'}
      </PlayButton>
      <AudioInfo>
        <AudioTitle>🎵 Audio Content</AudioTitle>
        <AudioSubtitle>
          {isCurrentlyPlaying ? 'Playing...' : 'Click to play audio'}
        </AudioSubtitle>
      </AudioInfo>
    </AudioPlayerContainer>
  );
};

// Lazy Image Component
interface LazyImageProps {
  src: string;
  alt: string;
  placeholder?: string;
  context?: 'single-post-main' | 'single-post-inline' | 'related-posts-grid';
  isMobile?: boolean;
}

const LazyImage: React.FC<LazyImageProps> = ({ 
  src, 
  alt, 
  placeholder = '🖼️', 
  context = 'single-post-inline',
  isMobile = false 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Use optimized image based on context
  const optimizedImage = useResponsiveImage(src, context, isMobile);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    console.warn('Optimized image failed, fallback to original:', optimizedImage.src);
    setHasError(true);
    setIsLoaded(false);
  };

  const imageSrc = hasError ? optimizedImage.originalSrc : optimizedImage.src;

  return (
    <ImageContainer>
      {!isLoaded && !hasError && (
        <ImagePlaceholder>
          {placeholder}
        </ImagePlaceholder>
      )}
      {hasError && (
        <ImagePlaceholder>
          📷
        </ImagePlaceholder>
      )}
      <PostImage
        src={imageSrc}
        alt={alt}
        $isLoaded={isLoaded}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        title={`${alt} - Optimized: ${optimizedImage.isOptimized ? optimizedImage.size : 'Original'} (${context})`}
      />
    </ImageContainer>
  );
};

// PC Hashtag Section Component
interface PCHashtagSectionProps {
  tagIds?: number[]; // Optional for backward compatibility
  tagDetails?: { id: number; name: string; slug: string; count: number; }[]; // New prop for embedded tags
}

const PCHashtagSection: React.FC<PCHashtagSectionProps> = ({ tagIds, tagDetails }) => {
  const [tags, setTags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // PRIORITY 1: Use embedded tagDetails if available (OPTIMIZED PATH)
    if (tagDetails && tagDetails.length > 0) {
      console.log('[PCHashtagSection] 🚀 Using embedded tagDetails, no API call needed:', tagDetails.length, 'tags');
      setTags(tagDetails.map(tag => ({
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
        count: tag.count,
        description: '',
        link: '',
        taxonomy: 'post_tag'
      })));
      setLoading(false);
      return;
    }

    // PRIORITY 2: No tags at all - show empty state
    if (!tagIds || tagIds.length === 0) {
      console.log('[PCHashtagSection] ℹ️ No tags available for this post');
      setLoading(false);
      return;
    }

    // PRIORITY 3: Use TagBatchManager to prevent API spam
    console.log('[PCHashtagSection] 🎯 Using TagBatchManager for', tagIds.length, 'tags (showing max 6)');
    
    const fetchTagsWithBatchManager = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const batchManager = TagBatchManager.getInstance();
        const limitedTagIds = tagIds.slice(0, 6); // Limit to 6 tags for UI space
        
        console.log('[PCHashtagSection] 🚀 Requesting tags via BatchManager:', limitedTagIds);
        
        // Use batch manager to get all tags efficiently
        const tagPromises = limitedTagIds.map(tagId => batchManager.getTag(tagId));
        const fetchedTags = await Promise.all(tagPromises);
        
        // Filter out null/undefined results
        const validTags = fetchedTags.filter(tag => tag !== null && tag !== undefined);
        
        console.log('[PCHashtagSection] ✅ TagBatchManager completed:', validTags.length, 'out of', limitedTagIds.length, 'tags');
        console.log('[PCHashtagSection] 📊 Cache stats:', batchManager.getCacheStats());
        
        setTags(validTags);
      } catch (err) {
        console.error('[PCHashtagSection] ❌ Error fetching tags:', err);
        setError('Unable to load hashtags');
      } finally {
        setLoading(false);
      }
    };

    fetchTagsWithBatchManager();
  }, [tagIds, tagDetails]);

  if ((!tagIds || tagIds.length === 0) && (!tagDetails || tagDetails.length === 0)) {
    return null;
  }

  return (
    <PCHashtagContainer>
                      <PCHashtagTitle>Article Tags</PCHashtagTitle>
      
      {loading && (
        <PCHashtagLoadingSkeleton>
          {Array.from({ length: Math.min((tagIds?.length || tagDetails?.length || 0), 6) }).map((_, index) => (
            <PCHashtagSkeletonItem 
              key={index} 
              $width={80 + Math.random() * 60} // Random width between 80-140px
            />
          ))}
        </PCHashtagLoadingSkeleton>
      )}

      {error && (
        <div style={{ 
          color: '#e53e3e', 
          fontSize: '14px', 
          textAlign: 'center',
          padding: '16px',
          background: 'rgba(229, 62, 62, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(229, 62, 62, 0.2)'
        }}>
          {error}
        </div>
      )}

      {!loading && !error && tags.length > 0 && (
        <PCHashtagGrid>
          {tags.map((tag) => (
            <PCHashtagItem
              key={tag.id}
              $isPopular={!!(tag.count && tag.count > 50)} // Popular if more than 50 posts
              onClick={() => navigate(`/tag/${tag.slug}`)}
                              title={`View all articles with tag ${tag.name}${tag.count ? ` (${tag.count} articles)` : ''}`}
            >
              {tag.name}
            </PCHashtagItem>
          ))}
        </PCHashtagGrid>
      )}

      {!loading && !error && tags.length === 0 && (
        <div style={{ 
          color: '#718096', 
          fontSize: '14px', 
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          No tags for this article
        </div>
      )}
    </PCHashtagContainer>
  );
  };

// Main component
const SinglePostFeed: React.FC = () => {
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPostId, setCurrentPostId] = useState<number | null>(null);
  const [scrollProgress, setScrollProgress] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const containerRef = useRef<HTMLDivElement>(null);
  const postRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const perPage = 10;
  const { getCurrentLanguageCategoryId, isInitialized: isLanguageInitialized } = useLanguageContext();
  const languageCategoryId = getCurrentLanguageCategoryId();
  const [error, setError] = useState<string | null>(null);

  // Global Audio Player State
  const [globalAudio, setGlobalAudio] = useState<GlobalAudioState>({
    isPlaying: false,
    currentPostId: null,
    currentAudioIndex: 0,
    currentAudio: null,
    playlist: []
  });

  // Helper functions (moved up to avoid dependency issues)
  const cleanHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  // ✅ MEMORY LEAK FIX: Track timeouts for cleanup
  const relatedPostsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const widgetScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // ✅ LAZY AUDIO LOADING: Cache for audio URL extraction to prevent spam requests during scroll
  const audioUrlCache = useRef<Map<string, string[]>>(new Map());
  const audioAvailabilityCache = useRef<Map<string, boolean>>(new Map());
  
  // ✅ NEW: Check if post has audio without extracting URLs (lightweight check)
  const hasAudioContent = useCallback((content: string): boolean => {
    // Use cache to prevent re-checking on every render
    if (audioAvailabilityCache.current.has(content)) {
      return audioAvailabilityCache.current.get(content)!;
    }
    
    // Lightweight check - just look for audio patterns without extracting URLs
    const hasAudio = /(<audio[^>]*src=["']([^"']*\.(?:mp3|wav|ogg|m4a))["'][^>]*>|<a[^>]*href=["']([^"']*\.(?:mp3|wav|ogg|m4a))["'][^>]*>|<p[^>]*>\s*https?:\/\/[^\s<>"]*\.(mp3|wav|ogg|m4a)\s*<\/p>)/i.test(content);
    
    audioAvailabilityCache.current.set(content, hasAudio);
    // ✅ REDUCED LOGGING: Only log in development mode to avoid console spam
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Audio availability check (cached):', hasAudio ? 'HAS AUDIO' : 'NO AUDIO', 'for content length:', content.length);
    }
    
    return hasAudio;
  }, []);
  
  // ✅ MODIFIED: Extract audio URLs only when actually needed (on play intent)
  const extractAudioUrls = useCallback((content: string): string[] => {
    // Use cache to prevent re-extraction
    if (audioUrlCache.current.has(content)) {
      console.log('🎵 Audio URLs retrieved from cache:', audioUrlCache.current.get(content)!.length, 'URLs');
      return audioUrlCache.current.get(content)!;
    }
    
    console.log('🎵 EXTRACTING audio URLs (only on play intent):', content.length, 'chars');
    
    const audioUrls: string[] = [];
    
    // Extract from audio elements
    const audioRegex = /<audio[^>]*src=["']([^"']*\.(?:mp3|wav|ogg|m4a))["'][^>]*>/gi;
    let match;
    while ((match = audioRegex.exec(content)) !== null) {
      audioUrls.push(match[1]);
    }
    
    // Extract from links to audio files
    const linkRegex = /<a[^>]*href=["']([^"']*\.(?:mp3|wav|ogg|m4a))["'][^>]*>/gi;
    while ((match = linkRegex.exec(content)) !== null) {
      audioUrls.push(match[1]);
    }
    
    // Extract from paragraph links
    const pRegex = /<p[^>]*>\s*https?:\/\/[^\s<>"]*\.(mp3|wav|ogg|m4a)\s*<\/p>/gi;
    while ((match = pRegex.exec(content)) !== null) {
      const urlMatch = match[0].match(/https?:\/\/[^\s<>"]*\.(mp3|wav|ogg|m4a)/i);
      if (urlMatch) {
        audioUrls.push(urlMatch[0]);
      }
    }
    
    const result = [...new Set(audioUrls)]; // Remove duplicates
    audioUrlCache.current.set(content, result); // Cache result
    console.log('🎵 Audio URLs extracted and cached:', result.length, 'URLs');
    
    return result;
  }, []);

  // ✅ LAZY PLAYLIST BUILDING: Only check for audio availability, don't extract URLs until play
  const buildPlaylist = useCallback((posts: WordPressPost[]) => {
    // ✅ NEW: Only check if posts have audio, don't extract URLs yet (prevents spam requests)
    const playlist = posts.map(post => ({
      postId: post.id,
      audioUrls: [], // ✅ Empty initially - will be populated on play intent
      title: cleanHtml(post.title.rendered || ''),
      hasAudio: post.audioUrl ? true : hasAudioContent(post.content?.rendered || ''), // ✅ Lightweight check only
      content: post.content?.rendered || '' // ✅ Store content for lazy extraction
    })).filter(item => item.hasAudio);
    
    setGlobalAudio(prev => ({ ...prev, playlist }));
    // ✅ REDUCED LOGGING: Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🎵 Playlist built (LAZY):', playlist.length, 'posts with audio detected (no URLs extracted yet)');
    }
  }, [hasAudioContent]);

  const stopAudio = useCallback(() => {
    setGlobalAudio(currentGlobalAudio => {
      if (currentGlobalAudio.currentAudio) {
        currentGlobalAudio.currentAudio.pause();
        currentGlobalAudio.currentAudio.currentTime = 0;
      }
      
      // Stop all other audio elements on the page
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach(audio => {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
      
      console.log('⏹️ All audio stopped');
      
      return {
        ...currentGlobalAudio,
        isPlaying: false,
        currentPostId: null,
        currentAudio: null
      };
    });
  }, []);

  // Forward declaration for playNextPost
  const playNextPostRef = useRef<() => void>();

  // ✅ LAZY AUDIO LOADING: Extract URLs only when user clicks play
  const playAudioFromPost = useCallback((postId: number) => {
    setGlobalAudio(currentGlobalAudio => {
      const playlistItem = currentGlobalAudio.playlist.find(item => item.postId === postId);
      if (!playlistItem || !playlistItem.hasAudio) {
        console.log('❌ No audio found for post:', postId);
        return currentGlobalAudio;
      }

      // ✅ LAZY EXTRACTION: Extract audio URLs only now (on play intent)
      let audioUrls = playlistItem.audioUrls;
      if (audioUrls.length === 0) {
        console.log('🎵 LAZY LOADING: Extracting audio URLs for post', postId, 'on play intent');
        // Try direct audioUrl first, then extract from content
        const post = posts.find(p => p.id === postId);
        if (post?.audioUrl) {
          audioUrls = [post.audioUrl];
        } else {
          audioUrls = extractAudioUrls(playlistItem.content);
        }
        
        // Update playlist with extracted URLs to cache for next time
        playlistItem.audioUrls = audioUrls;
        console.log('🎵 Audio URLs extracted on-demand:', audioUrls.length, 'URLs for post', postId);
      }

      if (audioUrls.length === 0) {
        console.log('❌ No audio URLs found after extraction for post:', postId);
        return currentGlobalAudio;
      }

      // Stop current audio if playing (from any source)
      if (currentGlobalAudio.currentAudio) {
        currentGlobalAudio.currentAudio.pause();
        currentGlobalAudio.currentAudio.currentTime = 0;
        console.log('⏹️ Stopped previous audio');
      }

      // Also stop any other audio elements that might be playing globally
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach(audio => {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
          console.log('⏹️ Stopped external audio element');
        }
      });

      // ✅ ENHANCED LAZY CREATION: Create audio element only when absolutely needed
      const audio = new Audio();
      audio.preload = 'none'; // Prevent automatic loading
      
      // ✅ CRITICAL FIX: Don't set src until immediately before play to prevent 206 requests
      // audio.src will be set right before play() call
      console.log('🎵 ULTRA-LAZY: Audio element created without src to prevent requests');
      
      // Set up the audio for playing
      const setupAndPlay = () => {
        audio.src = audioUrls[0];
        return audio.play();
      };
      
      // ✅ REMOVED audio.load() - let play() handle loading to prevent unwanted requests
      
      // ✅ ULTRA-LAZY PLAY: Set src and play only when user actually clicks
      setupAndPlay().then(() => {
        console.log('🎵 ULTRA-LAZY: Audio playing for post:', postId, playlistItem.title);
      }).catch(error => {
        console.error('❌ Error playing audio:', error);
      });

      // Handle audio end - auto-play next post
      audio.addEventListener('ended', () => {
        if (playNextPostRef.current) {
          playNextPostRef.current();
        }
      });

      return {
        ...currentGlobalAudio,
        isPlaying: true,
        currentPostId: postId,
        currentAudioIndex: 0,
        currentAudio: audio
      };
    });
  }, [extractAudioUrls, posts]);

  const toggleAudioForPost = useCallback((postId: number) => {
    setGlobalAudio(currentGlobalAudio => {
      if (currentGlobalAudio.isPlaying && currentGlobalAudio.currentPostId === postId) {
        // Stop audio
        if (currentGlobalAudio.currentAudio) {
          currentGlobalAudio.currentAudio.pause();
          currentGlobalAudio.currentAudio.currentTime = 0;
        }
        return {
          ...currentGlobalAudio,
          isPlaying: false,
          currentPostId: null,
          currentAudio: null
        };
      } else {
        // Play audio - will be handled by playAudioFromPost
        playAudioFromPost(postId);
        return currentGlobalAudio;
      }
    });
  }, [playAudioFromPost]);

  // Smooth scroll to post
  const scrollToPost = useCallback((postId: number) => {
    const postElement = postRefs.current[postId];
    if (postElement) {
      postElement.scrollIntoView(SCROLL_CONFIG);
      setCurrentPostId(postId);
      
      // Update URL without page reload - use window.history to avoid navigation
      setPosts(currentPosts => {
        const post = currentPosts.find(p => p.id === postId);
        if (post) {
          const newUrl = `/post/${post.id}/${post.slug}.html`;
          if (window.location.pathname !== newUrl) {
            window.history.replaceState(null, '', newUrl);
          }
        }
        return currentPosts; // Don't change posts state
      });
    }
  }, []); // Remove dependencies to prevent re-creation

  // PlayNextPost function
  const playNextPost = useCallback(() => {
    setGlobalAudio(currentGlobalAudio => {
      if (currentGlobalAudio.playlist.length === 0) return currentGlobalAudio;
      
      const currentIndex = currentGlobalAudio.playlist.findIndex(item => item.postId === currentGlobalAudio.currentPostId);
      const nextIndex = currentIndex + 1;
      
      if (nextIndex < currentGlobalAudio.playlist.length) {
        const nextPost = currentGlobalAudio.playlist[nextIndex];
        console.log('⏭️ Auto-playing next post:', nextPost.postId, nextPost.title);
        
        // Stop current audio
        if (currentGlobalAudio.currentAudio) {
          currentGlobalAudio.currentAudio.pause();
          currentGlobalAudio.currentAudio.currentTime = 0;
        }
        
        // ✅ LAZY LOADING: Extract URLs for next post if not already extracted
        let nextAudioUrls = nextPost.audioUrls;
        if (nextAudioUrls.length === 0) {
          console.log('🎵 LAZY LOADING: Extracting audio URLs for next post', nextPost.postId);
          const nextPostData = posts.find(p => p.id === nextPost.postId);
          if (nextPostData?.audioUrl) {
            nextAudioUrls = [nextPostData.audioUrl];
          } else {
            nextAudioUrls = extractAudioUrls(nextPost.content);
          }
          nextPost.audioUrls = nextAudioUrls; // Cache for future use
        }
        
        if (nextAudioUrls.length === 0) {
          console.log('❌ No audio URLs found for next post:', nextPost.postId);
          return {
            ...currentGlobalAudio,
            isPlaying: false,
            currentPostId: null,
            currentAudio: null
          };
        }
        
        // ✅ ENHANCED LAZY CREATION: Create audio element without src
        const audio = new Audio();
        audio.preload = 'none'; // Prevent automatic loading
        
        // ✅ ULTRA-LAZY NEXT: Set src only when actually playing
        const setupAndPlayNext = () => {
          audio.src = nextAudioUrls[0];
          return audio.play();
        };
        
        // ✅ REMOVED audio.load() - let play() handle loading to prevent unwanted requests
        
        setupAndPlayNext().then(() => {
          console.log('🎵 ULTRA-LAZY: Playing next audio:', nextPost.title);
        }).catch(error => {
          console.error('❌ Error playing next audio:', error);
        });
        
        // Handle audio end for next track
        audio.addEventListener('ended', () => {
          if (playNextPostRef.current) {
            playNextPostRef.current();
          }
        });
        
        // Scroll to next post
        scrollTimeoutRef.current = setTimeout(() => {
          scrollToPost(nextPost.postId);
          // ✅ MEMORY LEAK FIX: Clear timeout ref after execution
          scrollTimeoutRef.current = null;
        }, 100);
        
        return {
          ...currentGlobalAudio,
          isPlaying: true,
          currentPostId: nextPost.postId,
          currentAudioIndex: 0,
          currentAudio: audio
        };
      } else {
        // End of playlist
        console.log('🏁 End of playlist reached');
        return {
          ...currentGlobalAudio,
          isPlaying: false,
          currentPostId: null,
          currentAudio: null
        };
      }
    });
  }, [scrollToPost, extractAudioUrls, posts]);

  // Assign playNextPost to ref
  useEffect(() => {
    playNextPostRef.current = playNextPost;
  }, [playNextPost]);

  // Throttled scroll handler for progress bar and current post detection
  const handleScroll = useCallback(
    throttle(() => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
      
      // Debug logging (reduced to 1% to avoid spam)
      if (Math.random() < 0.01) { // Log only 1% of scroll events
        console.log('📊 Scroll:', {
          scrollTop: Math.round(scrollTop),
          progress: Math.round(progress),
          hasMore: document.body.dataset.hasMore,
          loadingMore: document.body.dataset.loadingMore
        });
      }

      // Detect current post in viewport
      const viewportCenter = scrollTop + window.innerHeight / 2;
      let currentPost: number | null = null;
      let minDistance = Infinity;

      // Find the post closest to viewport center
      Object.entries(postRefs.current).forEach(([postId, element]) => {
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementCenter = rect.top + window.pageYOffset + rect.height / 2;
          const distance = Math.abs(elementCenter - viewportCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            currentPost = parseInt(postId);
          }
        }
      });

      // Update current post and URL if changed
      if (currentPost !== null && currentPost !== parseInt(document.body.dataset.currentPostId || '0')) {
        document.body.dataset.currentPostId = String(currentPost);
        setCurrentPostId(currentPost);
        
        // Auto-scroll widget to current post
        widgetScrollTimeoutRef.current = setTimeout(() => {
          const playlistContainer = document.querySelector('[data-testid="playlist-container"]') as HTMLElement;
          const currentPlaylistItem = document.querySelector(`[data-post-id="${currentPost}"]`) as HTMLElement;
          
          if (playlistContainer && currentPlaylistItem) {
            const containerRect = playlistContainer.getBoundingClientRect();
            const itemRect = currentPlaylistItem.getBoundingClientRect();
            const scrollTop = playlistContainer.scrollTop;
            
            // Calculate position to center the item in the container
            const itemTop = itemRect.top - containerRect.top + scrollTop;
            const containerHeight = containerRect.height;
            const itemHeight = itemRect.height;
            const targetScrollTop = itemTop - (containerHeight / 2) + (itemHeight / 2);
            
            // Smooth scroll to the target position
            playlistContainer.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth'
            });
            
            // Debug log occasionally
            if (Math.random() < 0.1) {
              console.log('📋 Widget auto-scrolled to post:', currentPost);
            }
          }
          
          // ✅ MEMORY LEAK FIX: Clear timeout ref after execution
          widgetScrollTimeoutRef.current = null;
        }, 100); // Small delay to ensure DOM is updated
        
        // Update URL without page reload
        try {
          const postsData = document.body.dataset.posts;
          if (postsData) {
            const posts = JSON.parse(postsData);
            const post = posts.find((p: WordPressPost) => p.id === currentPost);
            if (post) {
              const newUrl = `/post/${post.id}/${post.slug}.html`;
              if (window.location.pathname !== newUrl) {
                window.history.replaceState(null, '', newUrl);
                // Only log URL changes occasionally to avoid spam
                if (Math.random() < 0.1) {
                  console.log('🔗 URL updated to:', newUrl);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error parsing posts data:', error);
        }
      }

      // Auto-load more when near bottom - enhanced detection
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const scrollPosition = scrollTop + clientHeight;
      const distanceFromBottom = scrollHeight - scrollPosition;
      
      // 🚀 PC OPTIMIZATION: More aggressive trigger - within 1200px of bottom for PC, 800px for mobile
      const triggerDistance = isMobile ? 800 : 1200;
      if (distanceFromBottom <= triggerDistance && scrollHeight > clientHeight) {
        const isLoadingMore = document.body.dataset.loadingMore === 'true';
        const hasMorePosts = document.body.dataset.hasMore === 'true';
        
        if (!isLoadingMore && hasMorePosts) {
          console.log('🔄 Auto-loading more posts... Distance from bottom:', Math.round(distanceFromBottom));
          console.log('📊 Scroll metrics:', {
            scrollHeight: Math.round(scrollHeight),
            clientHeight: Math.round(clientHeight),
            scrollPosition: Math.round(scrollPosition),
            distanceFromBottom: Math.round(distanceFromBottom)
          });
          
          // Call loadMorePosts directly without setting dataset flags
          // (loadMorePosts handles its own state management)
          loadMorePosts();
        }
      }
    }, 100),
    [] // Remove dependencies to prevent re-creation
  );

    // Load initial posts
  useEffect(() => {
    const loadInitialData = async () => {
      // ✅ Wait for language detection before loading data
      if (!isLanguageInitialized) {
        console.log('[SinglePostFeed] ⏳ Waiting for language detection...');
              return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('[SinglePostFeed] 🚀 Loading initial data...', {
          postId: id,
          languageCategoryId
        });

        let initialPosts: WordPressPost[] = [];

        if (id) {
          // Load specific post first
          const post = await fetchPostById(parseInt(id));
          if (post) {
            initialPosts = [post];
            
            // ✅ CRITICAL FIX: Load related posts instead of general posts
            console.log('[SinglePostFeed] 🔗 Loading related posts for single post view...', {
              postId: post.id,
              postTags: post.tags?.length || 0,
              languageCategoryId
            });
            
            try {
              // Use ONLY the related posts API - backend handles all fallback logic
              console.log('[SinglePostFeed] 🎯 Loading related posts using optimized API...');
              const relatedPosts = await fetchRelatedPosts(
                post.id,
                post.tags || [],
                19, // Load 19 related posts to get total of 20 with current post
                languageCategoryId || undefined
              );
              
              if (relatedPosts && relatedPosts.length > 0) {
                console.log('[SinglePostFeed] ✅ Related posts loaded from API:', {
                  relatedCount: relatedPosts.length,
                  totalPosts: initialPosts.length + relatedPosts.length
                });
                
                // Filter out current post if it appears in related results
                const filteredRelatedPosts = relatedPosts.filter(p => p.id !== parseInt(id));
                initialPosts = [...initialPosts, ...filteredRelatedPosts];
              } else {
                console.log('[SinglePostFeed] ⚠️ Related posts API returned empty - backend should handle fallback internally');
                // DO NOT call fetchPosts() here - let backend handle all fallback logic
                // This ensures no general posts API calls from single post pages
              }
            } catch (relatedError) {
              console.error('[SinglePostFeed] ❌ Error loading related posts:', relatedError);
              // DO NOT call fetchPosts() fallback - related posts API should be self-contained
              // This prevents the issue of calling general posts API from single post pages
            }
            
            setCurrentPostId(0);
          } else {
            throw new Error('Post not found');
          }
        } else {
          // Load latest posts with language filtering (for homepage/general view)
          console.log('[SinglePostFeed] 📰 Loading general posts for homepage view');
          initialPosts = await fetchPosts(1, 20, undefined, undefined, false, languageCategoryId || undefined);
          setCurrentPostId(0);
        }

        setPosts(initialPosts);
        setPage(2);
        setHasMore(initialPosts.length === 20);
        
        console.log('[SinglePostFeed] ✅ Initial data loaded:', {
          postsCount: initialPosts.length,
          currentPostIndex: 0,
          hasMore: initialPosts.length === 20,
          languageCategoryId,
          loadingStrategy: id ? 'related-posts' : 'general-posts'
        });

      } catch (err) {
        console.error('[SinglePostFeed] ❌ Error loading initial data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id, languageCategoryId, isLanguageInitialized]); // ✅ Added language dependencies

  // Load more posts - disabled for related posts mode
  const loadMorePosts = useCallback(async () => {
    // 🔗 RELATED POSTS MODE: When viewing a specific post, don't auto-load more posts
    // We only show curated related posts based on tags for better user experience
    if (currentPostId) {
      console.log('🚫 Auto-load disabled for related posts mode - showing curated content only');
      return;
    }

    // Get current state from dataset to avoid stale closures
    const isCurrentlyLoading = document.body.dataset.loadingMore === 'true';
    const hasMoreData = document.body.dataset.hasMore === 'true';
    
    if (isCurrentlyLoading || !hasMoreData) {
      console.log('🚫 Skipping load more:', { isCurrentlyLoading, hasMoreData });
      return;
    }
    
    // Set loading state immediately
    document.body.dataset.loadingMore = 'true';
    setLoadingMore(true);
    
    try {
      // Get current page from state
      setPage(currentPage => {
        const nextPage = currentPage + 1;
        console.log('📄 Loading page:', nextPage);
        
        // Load posts asynchronously
        fetchPosts(nextPage, perPage, undefined, undefined, false, languageCategoryId || undefined).then(newPosts => {
          console.log('📦 Fetched posts:', newPosts.length);
          
          if (newPosts.length > 0) {
            setPosts(prevPosts => {
              const existingIds = new Set(prevPosts.map(p => p.id));
              const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
              console.log('✅ Adding unique posts:', uniqueNewPosts.length);
              return [...prevPosts, ...uniqueNewPosts];
            });
            
            // Update hasMore based on returned posts
            const stillHasMore = newPosts.length === perPage;
            setHasMore(stillHasMore);
            document.body.dataset.hasMore = stillHasMore.toString();
            console.log('🔄 Has more posts:', stillHasMore);
          } else {
            setHasMore(false);
            document.body.dataset.hasMore = 'false';
            console.log('🏁 No more posts available');
          }
        }).catch(error => {
          console.error('❌ Error loading more posts:', error);
        }).finally(() => {
          setLoadingMore(false);
          document.body.dataset.loadingMore = 'false';
        });
        
        return nextPage;
      });
    } catch (error) {
      console.error('❌ Error in loadMorePosts:', error);
      setLoadingMore(false);
      document.body.dataset.loadingMore = 'false';
    }
  }, [currentPostId]); // Add currentPostId as dependency

  // Update dataset flags for scroll handler
  useEffect(() => {
    document.body.dataset.hasMore = hasMore.toString();
  }, [hasMore]);

  useEffect(() => {
    document.body.dataset.loadingMore = loadingMore.toString();
  }, [loadingMore]);

  // Sync posts data with dataset for scroll handler
  useEffect(() => {
    document.body.dataset.posts = JSON.stringify(posts);
  }, [posts]);

  // Build playlist when posts change
  useEffect(() => {
    if (posts.length > 0) {
      buildPlaylist(posts);
    }
  }, [posts, buildPlaylist]);

  // Global Audio Manager - Listen for any audio play events
  useEffect(() => {
    const handleAudioPlay = (event: Event) => {
      const playingAudio = event.target as HTMLAudioElement;
      
      // If this audio is not our current global audio, stop our global audio
      setGlobalAudio(currentGlobalAudio => {
        if (currentGlobalAudio.currentAudio && currentGlobalAudio.currentAudio !== playingAudio) {
          currentGlobalAudio.currentAudio.pause();
          currentGlobalAudio.currentAudio.currentTime = 0;
          console.log('⏹️ Global audio stopped due to external audio play');
          
          return {
            ...currentGlobalAudio,
            isPlaying: false,
            currentPostId: null,
            currentAudio: null
          };
        }
        return currentGlobalAudio;
      });
    };

    // Listen for any audio play events on the page
    document.addEventListener('play', handleAudioPlay, true);
    
    return () => {
      document.removeEventListener('play', handleAudioPlay, true);
    };
  }, []);

  // Clear audio caches when posts change significantly
  useEffect(() => {
    // Clear caches if we have more than 100 cached entries to prevent memory leak
    if (audioUrlCache.current.size > 100) {
      console.log('🧹 Clearing audio URL cache to prevent memory leak');
      audioUrlCache.current.clear();
    }
    if (audioAvailabilityCache.current.size > 200) {
      console.log('🧹 Clearing audio availability cache to prevent memory leak');
      audioAvailabilityCache.current.clear();
    }
  }, [posts.length]);

  // Scroll event listener & mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll]);

  // ✅ MEMORY LEAK FIX: Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (relatedPostsTimeoutRef.current) {
        clearTimeout(relatedPostsTimeoutRef.current);
        relatedPostsTimeoutRef.current = null;
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (widgetScrollTimeoutRef.current) {
        clearTimeout(widgetScrollTimeoutRef.current);
        widgetScrollTimeoutRef.current = null;
      }
    };
  }, []);

  // Clean content for display (remove audio elements but keep URLs for extraction)
  const cleanContent = (content: string): string => {
    let cleaned = content;
    
    // Remove video elements and URLs
    cleaned = cleaned.replace(/<video[^>]*>[\s\S]*?<\/video>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*class=["'].*?wp-block-video.*?["'][^>]*>[\s\S]*?<\/div>/gi, '');
    cleaned = cleaned.replace(/<p[^>]*>\s*https?:\/\/[^\s<>"]*\.(mp4|webm|avi|mov)\s*<\/p>/gi, '');
    
    // Remove subtitle files (.srt, .vtt) - both links and direct URLs
    cleaned = cleaned.replace(/<p[^>]*>\s*<a[^>]*href=["'][^"']*\.(srt|vtt)["'][^>]*>.*?<\/a>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<a[^>]*href=["'][^"']*\.(srt|vtt)["'][^>]*>.*?<\/a>/gi, '');
    cleaned = cleaned.replace(/<p[^>]*>\s*https?:\/\/[^\s<>"]*\.(srt|vtt)\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/https?:\/\/[^\s<>"]*\.(srt|vtt)/gi, '');
    
    // Remove "🎧 Nghe bài tóm tắt" - comprehensive removal including containers
    // Remove div containers with audio summary content
    cleaned = cleaned.replace(/<div[^>]*style=["'][^"']*background[^"']*["'][^>]*>[\s\S]*?🎧[\s\S]*?Nghe bài tóm tắt[\s\S]*?<\/div>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*>[\s\S]*?<h4[^>]*>[\s\S]*?🎧[\s\S]*?Nghe bài tóm tắt[\s\S]*?<\/h4>[\s\S]*?<\/div>/gi, '');
    // Remove h4 elements with audio summary
    cleaned = cleaned.replace(/<h4[^>]*>[\s\S]*?🎧[\s\S]*?Nghe bài tóm tắt[\s\S]*?<\/h4>/gi, '');
    // Remove paragraph and link patterns
    cleaned = cleaned.replace(/<p[^>]*>\s*<a[^>]*>.*?🎧.*?Nghe bài tóm tắt.*?<\/a>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<a[^>]*>.*?🎧.*?Nghe bài tóm tắt.*?<\/a>/gi, '');
    cleaned = cleaned.replace(/<p[^>]*>\s*🎧.*?Nghe bài tóm tắt[^\n\r]*<\/p>/gi, '');
    cleaned = cleaned.replace(/🎧.*?Nghe bài tóm tắt[^\n\r]*/gi, '');
    
    // Remove "🔗 Xem bài viết gốc" - comprehensive removal including containers  
    // Remove div containers with original post links
    cleaned = cleaned.replace(/<div[^>]*style=["'][^"']*background[^"']*["'][^>]*>[\s\S]*?🔗[\s\S]*?Xem bài.*?gốc[\s\S]*?<\/div>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*>[\s\S]*?<h4[^>]*>[\s\S]*?🔗[\s\S]*?Xem bài.*?gốc[\s\S]*?<\/h4>[\s\S]*?<\/div>/gi, '');
    // Remove h4 elements with original post links
    cleaned = cleaned.replace(/<h4[^>]*>[\s\S]*?🔗[\s\S]*?Xem bài.*?gốc[\s\S]*?<\/h4>/gi, '');
    // Remove paragraph and link patterns
    cleaned = cleaned.replace(/<p[^>]*>\s*<a[^>]*>.*?🔗.*?Xem bài.*?gốc.*?<\/a>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<p[^>]*>\s*<a[^>]*>.*?Xem bài.*?gốc.*?<\/a>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<a[^>]*>.*?🔗.*?Xem bài.*?gốc.*?<\/a>/gi, '');
    cleaned = cleaned.replace(/<a[^>]*>.*?Xem bài.*?gốc.*?<\/a>/gi, '');
    cleaned = cleaned.replace(/🔗.*?Xem bài.*?gốc[^\n\r]*/gi, '');
    cleaned = cleaned.replace(/Xem bài.*?gốc[^\n\r]*/gi, '');
    cleaned = cleaned.replace(/Xem bài gốc đi[^\n\r]*/gi, '');
    
    // Remove HTML5 audio elements (but keep URLs for custom player)
    cleaned = cleaned.replace(/<audio[^>]*controls[^>]*>[\s\S]*?<\/audio>/gi, '');
    cleaned = cleaned.replace(/<audio[^>]*>[\s\S]*?<\/audio>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*class=["'].*?wp-block-audio.*?["'][^>]*>[\s\S]*?<\/div>/gi, '');
    
    // Remove media file links in paragraphs (but keep for extraction)
    cleaned = cleaned.replace(/<p[^>]*>\s*<a[^>]*href=["'][^"']*\.(mp3|wav|ogg|m4a)["'][^>]*>.*?<\/a>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<p[^>]*>\s*https?:\/\/[^\s<>"]*\.(mp3|wav|ogg|m4a)\s*<\/p>/gi, '');
    
    // Remove empty elements
    cleaned = cleaned.replace(/<p[^>]*>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*>\s*<\/div>/gi, '');
    cleaned = cleaned.replace(/<span[^>]*>\s*<\/span>/gi, '');
    
    // Clean up line breaks
    cleaned = cleaned.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '<br />');
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n');
    
    return cleaned.trim();
  };



  // Get description for SEO
  const getDescription = (htmlContent: string) => {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    const text = div.textContent || '';
    return text.substring(0, 160) + (text.length > 160 ? '...' : '');
  };

  // Get image URL from post
  const getImageUrl = (post: WordPressPost): string => {
    if (post.imageUrls && Array.isArray(post.imageUrls) && post.imageUrls.length > 0) {
      return post.imageUrls[0];
    }
    
    if (post.content?.rendered) {
      const imgRegex = /<img[^>]+src=["']([^"'>]+)["'][^>]*>/gi;
      const matches = post.content.rendered.match(imgRegex);
      if (matches && matches.length > 0) {
        const srcMatch = matches[0].match(/src=["']([^"'>]+)["']/i);
        if (srcMatch && srcMatch[1]) {
          return srcMatch[1];
        }
      }
    }
    
    return 'https://via.placeholder.com/900x400/667eea/ffffff?text=Infonium';
  };

  // 🚀 Dispatch global event once the first batch of posts has rendered
  useEffect(() => {
    if (!loading) {
      // Give React one tick to commit DOM updates
      const timer = setTimeout(() => {
        (window as any).__POST_RENDER_COMPLETE__ = true;
        window.dispatchEvent(new Event('post-render-complete'));
        console.log('[SinglePostFeed] 📢 Dispatched post-render-complete');
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading) {
    return (
      <Container ref={containerRef}>
        <Header>
          <HeaderContent>
            <BrandSection>
              <LogoContainer onClick={() => window.location.href = '/'}>
                <Logo>
                  <LogoAccent>
                    <AtomOrbit 
                      $color="#e53e3e" 
                      $size={32} 
                      $duration={4}
                    />
                    <AtomOrbit 
                      $color="#38a169" 
                      $size={26} 
                      $duration={3.5}
                      $direction="reverse"
                    />
                    <AtomOrbit 
                      $color="#3182ce" 
                      $size={20} 
                      $duration={3}
                    />
                  </LogoAccent>
                </Logo>
                <BrandText>
                  <span className="infonium">INFONIUM</span>
                  <span className="org">.ORG</span>
                </BrandText>
              </LogoContainer>
            </BrandSection>
            <NavigationSection>
              <BackButton onClick={() => window.location.href = '/'}>
                ← Go to Homepage
              </BackButton>
            </NavigationSection>
          </HeaderContent>
        </Header>
        <LoadingContainer>
          <LoadingSpinner />
                      Loading articles...
        </LoadingContainer>
      </Container>
    );
  }

  if (posts.length === 0) {
    return (
      <Container ref={containerRef}>
        <Header>
          <HeaderContent>
            <BrandSection>
              <LogoContainer onClick={() => window.location.href = '/'}>
                <Logo>
                  <LogoAccent>
                    <AtomOrbit 
                      $color="#e53e3e" 
                      $size={32} 
                      $duration={4}
                    />
                    <AtomOrbit 
                      $color="#38a169" 
                      $size={26} 
                      $duration={3.5}
                      $direction="reverse"
                    />
                    <AtomOrbit 
                      $color="#3182ce" 
                      $size={20} 
                      $duration={3}
                    />
                  </LogoAccent>
                </Logo>
                <BrandText>
                  <span className="infonium">INFONIUM</span>
                  <span className="org">.ORG</span>
                </BrandText>
              </LogoContainer>
            </BrandSection>
            <NavigationSection>
              <BackButton onClick={() => window.location.href = '/'}>
                ← Go to Homepage
              </BackButton>
            </NavigationSection>
          </HeaderContent>
        </Header>
        <LoadingContainer>
                          <h3>Article not found</h3>
                      <p>Please try again later</p>
        </LoadingContainer>
      </Container>
    );
  }

  // Get current post for SEO
  const currentPost = posts.find(p => p.id === currentPostId) || posts[0];
  const title = currentPost.title.rendered ? cleanHtml(currentPost.title.rendered) : '';
  const description = getDescription(currentPost.content?.rendered || '');
  const currentUrl = window.location.href;
  const mainImageUrl = getImageUrl(currentPost);

  return (
    <Container ref={containerRef}>
      <Helmet>
        <title>{title} | Infonium</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`${title.toLowerCase()}, infonium, news, articles, high quality content`} />
        <meta name="author" content="Infonium" />
        <meta name="article:published_time" content={currentPost.date} />
        <meta name="article:modified_time" content={currentPost.date} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={mainImageUrl} />
        <meta property="og:site_name" content="Infonium" />
        <meta property="article:author" content="Infonium" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@infonium" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={mainImageUrl} />
        <link rel="canonical" href={currentUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="vi" />
      </Helmet>

      <ScrollProgress $progress={scrollProgress} />

      <Header>
        <HeaderContent>
          <BrandSection>
            <LogoContainer onClick={() => window.location.href = '/'}>
              <Logo>
                <LogoAccent>
                  <AtomOrbit 
                    $color="#e53e3e" 
                    $size={32} 
                    $duration={4}
                  />
                  <AtomOrbit 
                    $color="#38a169" 
                    $size={26} 
                    $duration={3.5}
                    $direction="reverse"
                  />
                  <AtomOrbit 
                    $color="#3182ce" 
                    $size={20} 
                    $duration={3}
                  />
                </LogoAccent>
              </Logo>
              <BrandText>
                <span className="infonium">INFONIUM</span>
                <span className="org">.ORG</span>
              </BrandText>
            </LogoContainer>
          </BrandSection>
          <NavigationSection>
            <BackButton onClick={() => window.location.href = '/'}>
              ← Go to Homepage
            </BackButton>
            <PostCounter>
              {posts.length} articles{hasMore ? '+' : ''}
            </PostCounter>
          </NavigationSection>
        </HeaderContent>
      </Header>

      <Breadcrumb>
                  <a href="/">Homepage</a>
        <span>/</span>
                    <span>{currentPostId ? 'Article Details' : 'Articles'}</span>
        <span>/</span>
        <span>{title.length > 50 ? title.substring(0, 50) + '...' : title}</span>
      </Breadcrumb>

      <MainContent>
        <ArticleSection>
            {posts.map((post, index) => {
              const postTitle = post.title.rendered ? cleanHtml(post.title.rendered) : '';
              // 🌍 USE ENHANCED API: Responsive content - mobileContent for mobile, cleanContent for desktop
              const rawContent = isMobile 
                ? (post.mobileContent || post.cleanContent || cleanContent(post.content.rendered || ''))
                : (post.cleanContent || cleanContent(post.content.rendered || ''));
              
              // 📱 CONTENT PROCESSING: Format sentences + mobile truncation (frontend only, API remains full)
              // PC needs HTML conversion for dangerouslySetInnerHTML, Mobile needs plain text
              const content = processContentForDisplay(rawContent, isMobile, 200, true);
              // ✅ LAZY LOADING: Only check if audio exists, don't extract URLs during render (prevents spam requests)
              const hasAudio = post.audioUrl ? true : hasAudioContent(post.content.rendered || '');
              const isCurrentPost = post.id === currentPostId;
            const imageUrl = getImageUrl(post);

              return (
                <PostContainer 
                  key={post.id}
                  ref={el => postRefs.current[post.id] = el as HTMLDivElement | null}
                  $isCurrent={isCurrentPost}
                >
                <LazyImage 
                  src={imageUrl} 
                  alt={postTitle} 
                  placeholder="📰" 
                  context="single-post-main"
                  isMobile={isMobile}
                />
                  
                  <PostContent>
                    <PostTitle>{postTitle}</PostTitle>
                    
                    <PostMeta>
                      <PostDate>{formatDate(post.date)}</PostDate>
                      <PostNumber>Article {index + 1}</PostNumber>
                    </PostMeta>
                    
                    {hasAudio && (
                      <CustomAudioPlayer audioUrls={[]} postTitle={postTitle} postId={post.id} globalAudio={globalAudio} onPlayAudio={playAudioFromPost} onStopAudio={stopAudio} />
                    )}
                    
                    {/* Google AdSense - PC Desktop below audio player */}
                    {!isMobile && (
                      <div style={{ margin: '20px 0', textAlign: 'center' }}>
                        <EmbeddedAdSense postId={post.id} />
                      </div>
                    )}
                    
                  <PostContentBody dangerouslySetInnerHTML={{ __html: content }} />
                    
                    {/* PC Hashtag Section */}
                    {post.tags && post.tags.length > 0 && (
                      <PCHashtagSection tagDetails={post.tagDetails} tagIds={post.tags} />
                    )}
                    
                    <PostSeparator />
                  </PostContent>
                </PostContainer>
              );
            })}
            
            {/* 💻 PC LOADING INDICATOR: Show elegant loading for better UX */}
            {loadingMore && !isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '20px',
                margin: '30px auto',
                maxWidth: '500px',
                color: '#fff',
                boxShadow: '0 15px 35px rgba(102, 126, 234, 0.3)',
                animation: 'fadeInScale 0.4s ease-out'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTop: '3px solid #fff',
                  borderRadius: '50%',
                  marginRight: '15px',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span style={{ 
                  fontSize: '18px',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  🔄 Loading more related articles...
                </span>
              </div>
            )}
            
            {/* Silent loading for mobile */}
            {loadingMore && isMobile && (
              <div style={{ height: '1px' }} />
            )}
            
            {/* End of content indicator */}
            {!hasMore && posts.length > 0 && (
              <EndOfContentContainer>
                <span>
                  {currentPostId ? 
                    `🔗 Showing all ${posts.length - 1} related articles` : 
                                          `🎯 Showing all ${posts.length} articles`
                  }
                </span>
                <BackToTopButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Back to Top
                </BackToTopButton>
              </EndOfContentContainer>
            )}
        </ArticleSection>

        <SidebarSection>
          <SidebarContainer>
            <SidebarHeader>
              <SidebarTitle>
                {currentPostId ? '🔗 Related Articles' : '📋 Article List'}
              </SidebarTitle>
              <GlobalAudioControls>
                <GlobalPlayButton
                  $isPlaying={globalAudio.isPlaying}
                  onClick={() => {
                    if (globalAudio.isPlaying) {
                      stopAudio();
                    } else if (globalAudio.playlist.length > 0) {
                      playAudioFromPost(globalAudio.playlist[0].postId);
                    }
                  }}
                  disabled={globalAudio.playlist.length === 0}
                  title={globalAudio.isPlaying ? 'Stop playlist' : 'Play playlist'}
                >
                  {globalAudio.isPlaying ? '⏹️' : '🎵'}
                </GlobalPlayButton>
                <GlobalPlaylistInfo>
                  {globalAudio.playlist.length} articles with audio
                  {globalAudio.isPlaying && globalAudio.currentPostId && (
                    <span> • Playing #{globalAudio.playlist.findIndex(item => item.postId === globalAudio.currentPostId) + 1}</span>
                  )}
                </GlobalPlaylistInfo>
              </GlobalAudioControls>
            </SidebarHeader>
            <PlaylistContainer data-testid="playlist-container">
              {posts.map((post, index) => {
                const isCurrent = post.id === currentPostId;
                const isPlaying = globalAudio.isPlaying && globalAudio.currentPostId === post.id;
                const postTitle = cleanHtml(post.title.rendered);
                const imageUrl = getImageUrl(post);
                const playlistItem = globalAudio.playlist.find(item => item.postId === post.id);
                const hasAudio = playlistItem?.hasAudio || false;
                
                return (
                  <PlaylistItem 
                    key={post.id}
                    data-post-id={post.id}
                    onClick={() => scrollToPost(post.id)}
                    $isCurrent={isCurrent}
                    $isPlaying={isPlaying}
                  >
                    <PlaylistNumber 
                      $isCurrent={isCurrent}
                      $isPlaying={isPlaying}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasAudio) {
                          toggleAudioForPost(post.id);
                        }
                      }}
                      title={hasAudio ? (isPlaying ? 'Pause audio' : 'Play audio') : 'No audio available'}
                    >
                      {isPlaying ? '⏸️' : (hasAudio ? '▶️' : (isCurrent ? '👁️' : index + 1))}
                    </PlaylistNumber>
                    <PlaylistImage
                      src={imageUrl}
                      alt={postTitle}
                      isMobile={isMobile}
                    />
                    <PlaylistInfo>
                      <PlaylistTitle $isCurrent={isCurrent} $isPlaying={isPlaying}>
                        {postTitle}
                        {hasAudio && <span style={{ marginLeft: '4px' }}>🎵</span>}
                      </PlaylistTitle>
                      <PlaylistDate $isCurrent={isCurrent} $isPlaying={isPlaying}>
                        {formatDate(post.date)}
                        {isPlaying && <span style={{ marginLeft: '4px' }}>Playing...</span>}
                      </PlaylistDate>
                    </PlaylistInfo>
                  </PlaylistItem>
                );
              })}
              
              {loadingMore && (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <LoadingSpinner style={{ width: '24px', height: '24px', marginBottom: '8px' }} />
                  <span style={{ fontSize: '0.8rem', color: '#718096' }}>Loading more...</span>
                </div>
              )}
            </PlaylistContainer>
          </SidebarContainer>
        </SidebarSection>
      </MainContent>
      
      {/* CSS animations for loading indicator */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(15px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </Container>
  );
};

export default SinglePostFeed; 